import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Product } from '../../services/shopService';
import { Button } from '../ui/button';
import { ShoppingCart, Eye } from 'lucide-react';
import { useToast } from '../../hooks/useToast';
import { useTenantContext } from '../../context/TenantContext';

interface ProductCardProps {
  product: Product;
}

export const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { toast } = useToast();
  const { tenant, previewConfig } = useTenantContext();
  
  // Récupérer la configuration personnalisée
  const cardConfig = useMemo(() => {
    const config = previewConfig?.pages?.shop?.productCard || tenant?.config?.pages?.shop?.productCard || {};
    return {
      // Grille
      gridColsMobile: config.gridColsMobile || '1',
      gridColsTablet: config.gridColsTablet || '2',
      gridColsDesktop: config.gridColsDesktop || '3',
      gridGap: config.gridGap || '16',
      // Style conteneur
      borderRadius: config.borderRadius || '8',
      borderWidth: config.borderWidth || '1',
      bgOpacity: config.bgOpacity || '5',
      shadowSize: config.shadowSize || '2xl',
      hoverScale: config.hoverScale || '102',
      transitionDuration: config.transitionDuration || '300',
      // Image
      imageAspectRatio: config.imageAspectRatio || 'square',
      imageObjectFit: config.imageObjectFit || 'cover',
      imageHoverScale: config.imageHoverScale || '110',
      imageOverlay: config.imageOverlay !== false,
      // Badge
      badgePosition: config.badgePosition || 'top-right',
      badgeTextUnique: config.badgeTextUnique || 'Produit Unique',
      badgeTextLimited: config.badgeTextLimited || 'Édition Limitée',
      badgeShow: config.badgeShow !== false,
      // Boutons
      buttonsPosition: config.buttonsPosition || 'center',
      buttonsGap: config.buttonsGap || '12',
      buttonsIconSize: config.buttonsIconSize || '18',
      buttonsShowOnHover: config.buttonsShowOnHover !== false,
      // Info
      infoPadding: config.infoPadding || '16',
      infoBgOpacity: config.infoBgOpacity || '5',
      infoTextAlign: config.infoTextAlign || 'left',
      titleSize: config.titleSize || 'xl',
      showStock: config.showStock !== false,
      // Effets créatifs
      glassEffect: config.glassEffect === true,
      neonBorder: config.neonBorder === true,
      floatingAnimation: config.floatingAnimation === true,
      tiltEffect: config.tiltEffect === true,
    };
  }, [tenant, previewConfig]);
  
  // Calculer les classes CSS dynamiques
  const aspectRatioClass = useMemo(() => {
    switch (cardConfig.imageAspectRatio) {
      case '4/3': return 'aspect-[4/3]';
      case '16/9': return 'aspect-video';
      case '3/4': return 'aspect-[3/4]';
      default: return 'aspect-square';
    }
  }, [cardConfig.imageAspectRatio]);
  
  const badgePositionClasses = useMemo(() => {
    switch (cardConfig.badgePosition) {
      case 'top-left': return 'top-2 left-2';
      case 'bottom-left': return 'bottom-2 left-2';
      case 'bottom-right': return 'bottom-2 right-2';
      default: return 'top-2 right-2';
    }
  }, [cardConfig.badgePosition]);
  
  const buttonsPositionClasses = useMemo(() => {
    switch (cardConfig.buttonsPosition) {
      case 'top': return 'top-4';
      case 'bottom': return 'bottom-4';
      default: return 'top-1/2 -translate-y-1/2';
    }
  }, [cardConfig.buttonsPosition]);
  
  // Trouver l'image mise en avant ou la première disponible
  const featuredImage = product.images?.find(img => img.is_featured) || product.images?.[0];
  const imageUrl = featuredImage?.image_url || '/images/placeholder-product.jpg';
  
  // Formater le prix en XAF
  const formattedPrice = product.price 
    ? new Intl.NumberFormat('fr-FR').format(product.price) + ' FCFA'
    : 'Prix sur demande';
    
  // Fonction pour ajouter au panier
  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Récupérer le panier actuel du localStorage
    const cartString = localStorage.getItem('shop_cart');
    const cart = cartString ? JSON.parse(cartString) : [];
    
    // Vérifier si le produit est déjà dans le panier
    const existingItemIndex = cart.findIndex((item: any) => item.productId === product.id);
    
    if (existingItemIndex >= 0) {
      // Mettre à jour la quantité si le produit existe déjà
      cart[existingItemIndex].quantity += 1;
    } else {
      // Ajouter le nouveau produit au panier
      cart.push({
        productId: product.id,
        title: product.title,
        price: product.price,
        currency: 'XAF',
        quantity: 1,
        imageUrl: imageUrl,
        slug: product.slug
      });
    }
    
    // Sauvegarder le panier mis à jour
    localStorage.setItem('shop_cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('cart-updated'));
    
    toast({
      title: 'Produit ajouté au panier',
      description: `${product.title} a été ajouté à votre panier. Voir le panier`,
      variant: 'default'
    });
  };

  return (
    <div 
      className={`group relative block backdrop-blur-sm overflow-hidden border text-white transition-all ${
        cardConfig.glassEffect ? 'bg-white/10 backdrop-blur-xl' : `bg-white/${cardConfig.bgOpacity}`
      } ${
        cardConfig.floatingAnimation ? 'animate-float' : ''
      } ${
        isHovered ? `shadow-${cardConfig.shadowSize} scale-[${parseFloat(cardConfig.hoverScale) / 100}]` : ''
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        borderRadius: `${cardConfig.borderRadius}px`,
        borderWidth: `${cardConfig.borderWidth}px`,
        borderColor: isHovered && cardConfig.neonBorder 
          ? tenant?.config?.color1 || '#fe8d17'
          : 'rgba(255, 255, 255, 0.1)',
        transitionDuration: `${cardConfig.transitionDuration}ms`,
        boxShadow: isHovered && cardConfig.neonBorder 
          ? `0 0 20px ${tenant?.config?.color1 || '#fe8d17'}80` 
          : undefined,
        transform: cardConfig.tiltEffect && isHovered 
          ? 'perspective(1000px) rotateY(5deg)' 
          : undefined,
      } as React.CSSProperties}
    >
      {/* Badge "Ressource Unique" ou "Édition Limitée" */}
      {cardConfig.badgeShow && (
      <div 
        className={`absolute ${badgePositionClasses} z-10 px-3 py-1 text-xs font-semibold rounded-full backdrop-blur-sm border transition-all duration-300 ${isHovered ? 'opacity-100 scale-100' : 'opacity-80 scale-95'}`}
        style={{
          backgroundColor: product.is_unique 
            ? `${tenant?.config?.color1 || '#fe8d17'}e6` 
            : 'rgba(255, 255, 255, 0.2)',
          borderColor: product.is_unique 
            ? tenant?.config?.color1 || '#fe8d17' 
            : 'rgba(255, 255, 255, 0.3)',
          color: product.is_unique 
            ? tenant?.config?.color3 || '#ffffff' 
            : tenant?.config?.color3 || '#ffffff'
        }}
      >
        {product.is_unique ? cardConfig.badgeTextUnique : cardConfig.badgeTextLimited}
      </div>
      )}
      
      {/* Image du produit - Cliquable vers la page détaillée */}
      <Link to={`/boutique/produit/${product.slug}`} className={`block ${aspectRatioClass} overflow-hidden relative`}>
        <img 
          src={imageUrl} 
          alt={product.title} 
          className={`h-full w-full transition-transform duration-500 cursor-pointer`}
          style={{
            objectFit: cardConfig.imageObjectFit as any,
            transform: isHovered ? `scale(${parseFloat(cardConfig.imageHoverScale) / 100})` : 'scale(1)',
          }}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.onerror = null;
            target.src = '/images/placeholder-product.jpg';
          }}
        />
        
        {/* Overlay au survol - à l'intérieur du lien */}
        {cardConfig.imageOverlay && (
        <div 
          className={`absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent transition-opacity duration-300 pointer-events-none ${
            isHovered ? 'opacity-100' : 'opacity-0'
          }`}
          style={{
            backgroundImage: isHovered ? `linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent, transparent)` : undefined
          }}
        />
        )}
      </Link>
      
      {/* Boutons d'action */}
      <div 
        className={`absolute ${buttonsPositionClasses} left-0 right-0 flex justify-center transform transition-all duration-300 ${
          cardConfig.buttonsShowOnHover 
            ? (isHovered ? 'opacity-100 scale-100' : 'opacity-0 scale-90') 
            : 'opacity-100 scale-100'
        }`}
        style={{ gap: `${cardConfig.buttonsGap}px` }}
      >
        <Button
          size="icon"
          variant="default"
          className="shadow-lg transition-all duration-200 active:scale-95 focus:ring-2"
          style={{
            backgroundColor: tenant?.config?.color1 || '#fe8d17',
            backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e07a10'})`,
            color: tenant?.config?.color3 || '#ffffff',
            '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
          } as React.CSSProperties}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#e07a10'}, ${tenant?.config?.color1 || '#fe8d17'})`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e07a10'})`;
          }}
          onClick={handleAddToCart}
          aria-label="Ajouter au panier"
        >
          <ShoppingCart size={parseInt(cardConfig.buttonsIconSize)} />
        </Button>
        
        <Link to={`/boutique/produit/${product.slug}`}>
          <Button
            size="icon"
            variant="secondary"
            className="backdrop-blur-sm shadow-lg transition-all duration-200 active:scale-95 focus:ring-2 border"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              color: tenant?.config?.color3 || '#ffffff',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
            } as React.CSSProperties}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
            }}
            aria-label="Voir le produit"
          >
            <Eye size={parseInt(cardConfig.buttonsIconSize)} />
          </Button>
        </Link>
      </div>
      
      {/* Informations du produit */}
      <div 
        className="backdrop-blur-sm border-t border-white/10"
        style={{
          padding: `${cardConfig.infoPadding}px`,
          backgroundColor: `rgba(255, 255, 255, ${parseFloat(cardConfig.infoBgOpacity) / 100})`,
          textAlign: cardConfig.infoTextAlign as any,
        }}
      >
        <Link to={`/boutique/produit/${product.slug}`}>
          <h3 className={`text-${cardConfig.titleSize} font-bold leading-tight mb-2 hover:scale-[1.02] transform transition-transform duration-300`}
            style={{
              color: tenant?.config?.color3 || '#ffffff',
              '--tw-text-color': isHovered ? (tenant?.config?.color1 || '#fe8d17') : (tenant?.config?.color3 || '#ffffff')
            } as React.CSSProperties}
          >
            {product.title}
          </h3>
        </Link>
        
        <div className="flex items-center justify-between mt-3">
          <p className="text-sm font-medium px-3 py-1 rounded-full backdrop-blur-sm"
            style={{
              color: tenant?.config?.color3 || '#ffffff',
              backgroundColor: `${tenant?.config?.color3 || '#fe8d17'}1a`,
              borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`
            }}
          >
            {product.category?.name || 'Informatique'}
          </p>
          <div className="px-3 py-1 rounded-full text-sm font-bold shadow-lg transform group-hover:scale-105 transition-transform duration-300"
            style={{
              backgroundColor: tenant?.config?.color1 || '#fe8d17',
              backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e07a10'})`,
              color: tenant?.config?.color3 || '#ffffff'
            }}
          >
            {formattedPrice}
          </div>
        </div>
        
        {cardConfig.showStock && product.stock_quantity !== undefined && (
          <div className="mt-2 text-xs" style={{ color: `${tenant?.config?.color1 || '#9ca3af'}99` }}>
            Stock: {product.stock_quantity} disponible{product.stock_quantity > 1 ? 's' : ''}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductCard;
